import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, UploadSimple, DownloadSimple, ClipboardText, Paperclip, StackSimple, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";

const EMPTY = { client_id: "", report_month: "", title: "", notes: "", file_name: "", file_url: "", size: 0, content_type: "" };

function monthLabel(m) {
  if (!m || m.length !== 7) return m || "—";
  const [y, mm] = m.split("-");
  const d = new Date(Number(y), Number(mm) - 1, 1);
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

function fileHref(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}

function humanSize(bytes) {
  if (!bytes) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
}

export default function ClientReportsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [filterClient, setFilterClient] = useState("all");
  const [filterMonth, setFilterMonth] = useState("");

  // Bulk-upload state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkClient, setBulkClient] = useState("");
  const [bulkMonth, setBulkMonth] = useState("");
  const [bulkPrefix, setBulkPrefix] = useState("");
  const [bulkQueue, setBulkQueue] = useState([]); // [{ file, status, error, title }]
  const [bulkRunning, setBulkRunning] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (filterClient !== "all") params.client_id = filterClient;
      if (filterMonth) params.month = filterMonth;
      const [r, c] = await Promise.all([
        api.get("/client-reports", { params }),
        api.get("/clients"),
      ]);
      setItems(r.data);
      setClients(c.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClient, filterMonth]);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.name])), [clients]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/client_reports`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body,
      });
      if (!res.ok) throw new Error("upload failed");
      const data = await res.json();
      setForm((f) => ({ ...f, file_name: data.file_name, file_url: data.file_url, size: data.size, content_type: data.content_type }));
      toast.success("File attached — fill the details and Save");
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (!form.report_month) { toast.error("Pick the month"); return; }
    if (!form.file_url) { toast.error("Attach the report file first"); return; }
    if (!form.title) { toast.error("Give this report a title"); return; }
    try {
      await api.post("/client-reports", form);
      toast.success("Report saved");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  }

  function pickBulkFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBulkQueue((q) => [
      ...q,
      ...files.map((f) => ({ file: f, status: "queued", title: (bulkPrefix ? bulkPrefix + " — " : "") + f.name.replace(/\.[^.]+$/, "") })),
    ]);
  }

  function stripExt(name) { return (name || "").replace(/\.[^.]+$/, ""); }

  async function runBulk() {
    if (!bulkClient) { toast.error("Pick a client for all files"); return; }
    if (!bulkMonth) { toast.error("Pick the month for all files"); return; }
    if (!bulkQueue.length) { toast.error("Add at least one file"); return; }
    setBulkRunning(true);
    const token = localStorage.getItem("access_token");
    let done = 0, failed = 0;
    for (let i = 0; i < bulkQueue.length; i++) {
      const entry = bulkQueue[i];
      if (entry.status === "done") continue;
      setBulkQueue((q) => q.map((e, idx) => idx === i ? { ...e, status: "uploading", error: "" } : e));
      try {
        const body = new FormData();
        body.append("file", entry.file);
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/client_reports`, {
          method: "POST", headers: { Authorization: `Bearer ${token}` }, body,
        });
        if (!res.ok) throw new Error("upload failed");
        const up = await res.json();
        await api.post("/client-reports", {
          client_id: bulkClient,
          report_month: bulkMonth,
          title: entry.title || stripExt(entry.file.name),
          notes: "",
          file_name: up.file_name,
          file_url: up.file_url,
          size: up.size,
          content_type: up.content_type,
        });
        done += 1;
        setBulkQueue((q) => q.map((e, idx) => idx === i ? { ...e, status: "done" } : e));
      } catch (err) {
        failed += 1;
        const detail = err.response?.data?.detail || err.message || "failed";
        setBulkQueue((q) => q.map((e, idx) => idx === i ? { ...e, status: "failed", error: detail } : e));
      }
    }
    setBulkRunning(false);
    toast[failed ? "warning" : "success"](`Bulk upload finished — ${done} saved, ${failed} failed`);
    load();
    if (!failed) closeBulk();
  }

  function closeBulk() {
    setBulkOpen(false);
    setBulkClient(""); setBulkMonth(""); setBulkPrefix(""); setBulkQueue([]); setBulkRunning(false);
  }

  return (
    <div className="space-y-6" data-testid="client-reports-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><ClipboardText size={12} /> Client Reports</div>
          <h1 className="section-title text-3xl sm:text-4xl">Uploaded Reports per Client</h1>
          <p className="text-sm text-muted-foreground mt-1">Store monthly deliverables, statements, or PDFs against a specific client and month for later reference.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-border" data-testid="bulk-upload-btn">
            <StackSimple size={16} className="mr-2" /> Bulk Upload
          </Button>
          <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-client-report-btn">
            <Plus size={16} className="mr-2" /> Upload Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Client</Label>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-[240px] border-border" data-testid="filter-client"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Month</Label>
          <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="border-border w-[180px]" data-testid="filter-month" />
        </div>
        {(filterClient !== "all" || filterMonth) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterClient("all"); setFilterMonth(""); }} data-testid="clear-filters">Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>File</TableHead>
              <TableHead>Uploaded By</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No reports yet. Click Upload Report to add the first one.</TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id} data-testid={`client-report-row-${r.id}`}>
                <TableCell className="font-medium tabular-nums">{monthLabel(r.report_month)}</TableCell>
                <TableCell>{r.client_name || clientMap[r.client_id] || "—"}</TableCell>
                <TableCell>
                  <div>{r.title}</div>
                  {r.notes && <div className="text-xs text-muted-foreground mt-0.5 max-w-md truncate" title={r.notes}>{r.notes}</div>}
                </TableCell>
                <TableCell>
                  <a href={fileHref(r.file_url)} target="_blank" rel="noreferrer" className="text-sm hover:underline flex items-center gap-1.5" data-testid={`download-report-${r.id}`}>
                    <DownloadSimple size={14} /> {r.file_name || "download"}
                    {r.size ? <span className="text-[10px] text-muted-foreground">({humanSize(r.size)})</span> : null}
                  </a>
                </TableCell>
                <TableCell className="text-sm">{r.uploaded_by_name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{(r.created_at || "").slice(0, 10)}</TableCell>
                <TableCell className="text-right">
                  {(user?.role === "admin" || r.uploaded_by === user?.id) && !r.deleted_at && (
                    <ArchiveButton endpoint={`/client-reports/${r.id}`} label={r.title} onArchived={load} testId={`archive-report-${r.id}`} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="client-report-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black">Upload Client Report</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="report-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Report Month</Label>
                <Input type="month" required value={form.report_month} onChange={(e) => setForm({ ...form, report_month: e.target.value })} className="border-border" data-testid="report-month-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Monthly Statement of Account" className="border-border" data-testid="report-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" data-testid="report-notes-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">File</Label>
              {form.file_url ? (
                <div className="flex items-center justify-between border border-border rounded px-3 py-2 text-sm bg-muted/30">
                  <span className="flex items-center gap-2 truncate"><Paperclip size={14} /> {form.file_name} <span className="text-[10px] text-muted-foreground">({humanSize(form.size)})</span></span>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, file_name: "", file_url: "", size: 0 })}>Replace</Button>
                </div>
              ) : (
                <label className="border border-dashed border-border rounded p-4 flex flex-col items-center gap-1 cursor-pointer hover:bg-muted/40 transition text-sm" data-testid="report-file-label">
                  <UploadSimple size={20} className="text-muted-foreground" />
                  <span>{uploading ? "Uploading…" : "Click to select PDF / DOCX / XLSX"}</span>
                  <input type="file" onChange={handleFile} className="hidden" disabled={uploading} data-testid="report-file-input" />
                </label>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={uploading} data-testid="report-submit-btn">Save report</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk upload dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => v ? setBulkOpen(true) : closeBulk()}>
        <DialogContent className="max-w-2xl" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Bulk Upload Reports</DialogTitle>
            <p className="text-xs text-muted-foreground">Drop several files at once — they'll all be tagged with the same client and month. Title defaults to the file name.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Client (all files)</Label>
                <Select value={bulkClient} onValueChange={setBulkClient}>
                  <SelectTrigger data-testid="bulk-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Month (all files)</Label>
                <Input type="month" value={bulkMonth} onChange={(e) => setBulkMonth(e.target.value)} className="border-border" data-testid="bulk-month-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Title prefix (optional)</Label>
                <Input value={bulkPrefix} onChange={(e) => setBulkPrefix(e.target.value)} placeholder="e.g. Feb 2026" className="border-border" data-testid="bulk-prefix-input" />
              </div>
            </div>

            <label className="border border-dashed border-border rounded p-6 flex flex-col items-center gap-2 cursor-pointer hover:bg-muted/40 transition text-sm" data-testid="bulk-file-label">
              <UploadSimple size={22} className="text-muted-foreground" />
              <span>Click to add files (you can add more later)</span>
              <span className="text-[10px] text-muted-foreground">PDF, DOCX, XLSX, images — anything works</span>
              <input type="file" multiple onChange={pickBulkFiles} className="hidden" data-testid="bulk-file-input" />
            </label>

            {bulkQueue.length > 0 && (
              <div className="border border-border rounded max-h-72 overflow-y-auto divide-y divide-border">
                {bulkQueue.map((entry, i) => (
                  <div key={i} className="p-3 flex items-center gap-3" data-testid={`bulk-row-${i}`}>
                    <span className="shrink-0">
                      {entry.status === "queued" && <Paperclip size={16} className="text-muted-foreground" />}
                      {entry.status === "uploading" && <CircleNotch size={16} className="animate-spin text-muted-foreground" />}
                      {entry.status === "done" && <CheckCircle size={16} weight="fill" className="text-emerald-600" />}
                      {entry.status === "failed" && <XCircle size={16} weight="fill" className="text-rose-600" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate">{entry.file.name} <span className="text-[10px] text-muted-foreground">({humanSize(entry.file.size)})</span></div>
                      <Input
                        value={entry.title}
                        onChange={(e) => setBulkQueue((q) => q.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x))}
                        placeholder="Title"
                        className="border-border h-7 text-xs mt-1"
                        disabled={bulkRunning || entry.status === "done"}
                        data-testid={`bulk-title-${i}`}
                      />
                      {entry.error && <div className="text-[10px] text-rose-600 mt-1">{entry.error}</div>}
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setBulkQueue((q) => q.filter((_, idx) => idx !== i))} disabled={bulkRunning} data-testid={`bulk-remove-${i}`}>Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeBulk} disabled={bulkRunning}>Close</Button>
            <Button type="button" onClick={runBulk} disabled={bulkRunning || bulkQueue.length === 0} data-testid="bulk-run-btn">
              {bulkRunning ? "Uploading…" : `Upload ${bulkQueue.filter((x) => x.status !== "done").length || bulkQueue.length} file(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
